/**
 * export_flow tool - Export a flow as a package
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const exportFlowInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID) to export"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
});

export type ExportFlowInput = z.infer<typeof exportFlowInputSchema>;

export const exportFlowTool = {
  name: "export_flow",
  description:
    "Export a flow as a package. Returns the flow definition and metadata that can be used to import into another environment or as a backup.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID) to export",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
    },
    required: ["flowId"],
  },
};

export async function handleExportFlow(
  context: { api: FlowManagementApi | null; adminApi: unknown },
  input: unknown
): Promise<string> {
  const parsed = exportFlowInputSchema.parse(input);
  const api = context.api;
  if (!api) throw new Error("Flow API not configured");

  logger.info({ flowId: parsed.flowId }, "Executing export_flow");

  const admin = context.adminApi as {
    exportFlowPackage?: (
      e: string,
      f: string,
      o?: { displayName?: string; description?: string },
    ) => Promise<{ packageLink?: { value?: string }; status?: string }>;
  } | null;
  const envId =
    parsed.environment ?? (api as unknown as { defaultEnvironmentId: string }).defaultEnvironmentId;

  const exportData = admin?.exportFlowPackage
    ? await admin.exportFlowPackage(envId, parsed.flowId)
    : await api.exportFlow(parsed.flowId, parsed.environment);

  const data = (exportData ?? {}) as { packageLink?: { value?: string }; status?: string };
  const link = data.packageLink?.value;
  const status = data.status ?? "Unknown";
  const lines = [`Exported flow ${parsed.flowId} — status: ${status}.`];
  if (link) {
    lines.push(`Download the package (zip: definition.json, apisMap.json, connectionsMap.json, manifests):`);
    lines.push(`  ${link}`);
    const expiry = /[?&]se=([^&]+)/.exec(link)?.[1];
    if (expiry) {
      const when = new Date(decodeURIComponent(expiry));
      if (!Number.isNaN(when.getTime())) {
        lines.push(`  The link is signed and expires ${when.toISOString()} — download it before then; run export_flow again for a fresh link.`);
      }
    }
    lines.push(`Import it into another environment with import_solution (for solution flows) or the Power Automate portal's "Import package".`);
  } else {
    lines.push(`The service did not return a download link${status === "Succeeded" ? "" : ` (status ${status})`}; run export_flow again in a moment.`);
  }
  return lines.join("\n");
}
